import { useState } from "react";
import {
  MdChatBubbleOutline,
  MdCheck,
  MdIosShare,
  MdLink,
  MdMoreHoriz,
  MdOutlineReportProblem,
} from "react-icons/md";

import type { DropdownMenuItem } from "MVVM/Components/DropdownMenu";
import { FeedbackModal } from "MVVM/Components/FeedbackModal";
import { trackMatch } from "MVVM/Views/Match/trackMatch";
import { useCopyLink } from "MVVM/Views/Match/useCopyLink";
import {
  MatchMenuButton,
  MatchMenuWrap,
} from "MVVM/Views/Match/styledComponents";

type TShareMatchButtonProps = {
  matchId: string;
  playerGameId: string;
  tabName: string;
};

// The route is public but player-scoped, and the ViewModel falls back to the
// viewer's own steamid when playerGameId is missing. A link without it shows a
// signed-out recipient someone else's match, or nothing at all.
export const buildShareUrl = (
  matchId: string,
  playerGameId: string,
  tabName: string,
) => {
  const url = new URL(`${window.location.origin}/match/${matchId}`);
  if (playerGameId) url.searchParams.set("playerGameId", playerGameId);
  url.searchParams.set("tab", tabName);
  url.searchParams.set("utm_source", "match_share");
  return url.toString();
};

export const MatchMenu = ({
  matchId,
  playerGameId,
  tabName,
}: TShareMatchButtonProps) => {
  const { copied, copy } = useCopyLink();
  const [feedback, setFeedback] = useState<"match" | "match-data" | null>(null);
  const url = buildShareUrl(matchId, playerGameId, tabName);
  const used = (item: string) => trackMatch("Menu Used", { item });
  // The system share sheet, where the device has one (mostly phones).
  const canShare = typeof navigator !== "undefined" && !!navigator.share;

  const items: DropdownMenuItem[] = [
    {
      id: "copy",
      label: copied ? "Link copied" : "Copy link",
      icon: copied ? <MdCheck /> : <MdLink />,
      onClick: () => {
        used("copy");
        copy(url);
      },
    },
    ...(canShare
      ? [
          {
            id: "share",
            label: "Share",
            icon: <MdIosShare />,
            onClick: () => {
              used("share");
              navigator.share({ url }).catch(() => undefined);
            },
          },
        ]
      : []),
    {
      id: "feedback",
      label: "Give feedback",
      icon: <MdChatBubbleOutline />,
      onClick: () => {
        used("feedback");
        setFeedback("match");
      },
    },
    {
      id: "report",
      label: "Report a problem with this match",
      icon: <MdOutlineReportProblem />,
      onClick: () => {
        used("report");
        setFeedback("match-data");
      },
    },
  ];

  return (
    <>
      <MatchMenuWrap
        items={items}
        trigger={
          <MatchMenuButton type="button" aria-label="Match options">
            <MdMoreHoriz size={20} aria-hidden />
          </MatchMenuButton>
        }
      />
      <FeedbackModal
        open={!!feedback}
        onOpenChange={(open) => !open && setFeedback(null)}
        hideTrigger
        topic={feedback || "match"}
        context={`match ${matchId} player ${playerGameId} tab ${tabName}`}
      />
    </>
  );
};
